// web/src/screens/admin/logs_common.ts
//
// Shared rendering helpers for the admin "Game log" screens (sessions_list,
// sessions_detail, attempts_detail). All builders use textContent only —
// these helpers are part of the T-11-09a StoredXSS mitigation surface since
// every value flowing through them comes from the API.

// navigateAdmin — history.pushState + synthetic popstate, identical to the
// helper duplicated across the other admin screens (main.ts's dispatch
// listens for popstate).
export function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// fmtMicrousd renders a microUSD integer as a $ amount with 4 decimal
// places (the turn cost is ~$0.0001, so 4 dp keeps it visible). null /
// undefined → "—".
export function fmtMicrousd(v: number | null | undefined): string {
  if (v == null) return "—";
  return `$${(v / 1_000_000).toFixed(4)}`;
}

// fmtTimestamp renders an RFC3339 string in the viewer's locale, falling
// back to the raw string if it doesn't parse. Empty/null → "—".
export function fmtTimestamp(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

// evalColors maps an evaluation bucket to (text colour, background tint) for
// pill / cell rendering. Mirrors v1's pico-variable choices: correct=green,
// partial=amber, incorrect=red, silence=gray.
export function evalColors(ev: string): { fg: string; bg: string } {
  switch (ev) {
    case "correct":
      return { fg: "#166534", bg: "#dcfce7" };
    case "partial":
      return { fg: "#92400e", bg: "#fef3c7" };
    case "incorrect":
      return { fg: "#991b1b", bg: "#fee2e2" };
    case "silence":
      return { fg: "#374151", bg: "#f3f4f6" };
    default:
      return { fg: "#374151", bg: "#f3f4f6" };
  }
}

// pill builds a small rounded label with the given text + colours.
export function pill(text: string, fg: string, bg: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.textContent = text; // textContent
  s.setAttribute(
    "style",
    `padding: 2px 8px; border-radius: 999px; background: ${bg}; color: ${fg}; font-size: 12px; white-space: nowrap;`,
  );
  return s;
}

// statusPill renders "finished" (green) / "in_progress" (gray) for a session.
export function statusPill(status: string): HTMLSpanElement {
  return status === "finished" ? pill("finished", "#166534", "#dcfce7") : pill("in progress", "#374151", "#f3f4f6");
}

// kvList builds a <dl> from [label, value-node|string] pairs. String values
// are inserted as textContent (a <dd>'s text); node values are appended.
export function kvList(pairs: Array<[string, string | Node]>): HTMLDListElement {
  const dl = document.createElement("dl");
  dl.setAttribute("style", "display: grid; grid-template-columns: max-content 1fr; gap: 4px 16px; margin: 0; font-size: 14px;");
  for (const [label, value] of pairs) {
    const dt = document.createElement("dt");
    dt.textContent = label;
    dt.setAttribute("style", "color: #6b7280; font-weight: 500;");
    dl.appendChild(dt);
    const dd = document.createElement("dd");
    dd.setAttribute("style", "margin: 0; color: #111827;");
    if (typeof value === "string") {
      dd.textContent = value;
    } else {
      dd.appendChild(value);
    }
    dl.appendChild(dd);
  }
  return dl;
}

// code wraps a string in a monospace <code> span (textContent).
export function code(text: string | null | undefined): HTMLElement {
  const c = document.createElement("code");
  c.textContent = text == null || text === "" ? "—" : text;
  c.setAttribute("style", "font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; word-break: break-all;");
  return c;
}

// section builds a titled card (<section> with an <h2> heading) and returns
// the body element to populate.
export function section(parent: HTMLElement, title: string): HTMLElement {
  const sec = document.createElement("section");
  sec.setAttribute("style", "background: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; margin-bottom: 16px;");
  const h = document.createElement("h2");
  h.textContent = title;
  h.setAttribute("style", "margin: 0 0 12px 0; font-size: 15px; font-weight: 600;");
  sec.appendChild(h);
  const body = document.createElement("div");
  sec.appendChild(body);
  parent.appendChild(sec);
  return body;
}

// table builds a <table> with the given header labels and returns
// { el, addRow } where addRow(cells) appends one <tr>. Each cell may be a
// string (textContent of a <td>) or a Node (appended into a <td>).
export function table(headers: string[]): { el: HTMLTableElement; addRow: (cells: Array<string | Node>) => HTMLTableRowElement } {
  const t = document.createElement("table");
  t.setAttribute("style", "border-collapse: collapse; width: 100%; font-size: 13px;");
  const thead = document.createElement("thead");
  const htr = document.createElement("tr");
  for (const h of headers) {
    const th = document.createElement("th");
    th.textContent = h;
    th.setAttribute("style", "text-align: left; padding: 6px 10px; border-bottom: 2px solid #e5e7eb; color: #6b7280; white-space: nowrap;");
    htr.appendChild(th);
  }
  thead.appendChild(htr);
  t.appendChild(thead);
  const tbody = document.createElement("tbody");
  t.appendChild(tbody);
  function addRow(cells: Array<string | Node>): HTMLTableRowElement {
    const tr = document.createElement("tr");
    for (const c of cells) {
      const td = document.createElement("td");
      td.setAttribute("style", "padding: 6px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: top;");
      if (typeof c === "string") {
        td.textContent = c;
      } else {
        td.appendChild(c);
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
    return tr;
  }
  return { el: t, addRow };
}

// adminLink builds an in-app <a> that pushes a path via navigateAdmin
// instead of doing a full navigation. text is set via textContent.
export function adminLink(text: string, href: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.textContent = text;
  a.setAttribute("href", href);
  a.setAttribute("style", "color: #1d4ed8; text-decoration: none;");
  a.addEventListener("click", (e) => {
    e.preventDefault();
    navigateAdmin(href);
  });
  return a;
}

// errMessage maps an unknown caught error to a user-facing status string,
// special-casing the 401/403 re-auth case.
export function errMessage(err: unknown, what: string): string {
  // AdminError carries .status; avoid importing it here to keep this file
  // dependency-free — duck-type on the shape instead.
  const e = err as { status?: number; message?: string };
  if (e && (e.status === 401 || e.status === 403)) {
    return "Not signed in — visit /login to authenticate, then refresh.";
  }
  return `Failed to ${what}: ${e?.message ?? String(err)}`;
}
